// build browser-only: webpack 4 ignora el campo `exports`, resuelve `main` (build dual
// node+browser) y falla al importar node:crypto/node:fs (PB-121)
const initSqlJs = require('sql.js/dist/sql-wasm-browser.js');
console.log('[gpkg-debug]', typeof initSqlJs, 'default:', initSqlJs && typeof initSqlJs.default,
  'Module:', initSqlJs && typeof initSqlJs.Module, 'keys:', initSqlJs && Object.keys(initSqlJs));
import TileLayer from 'ol/layer/Tile.js';
import XYZ from 'ol/source/XYZ.js';
import VectorTileLayer from 'ol/layer/VectorTile.js';
import VectorTileSource from 'ol/source/VectorTile.js';
import MVT from 'ol/format/MVT.js';
import TileGrid from 'ol/tilegrid/TileGrid.js';
import { gunzipSync } from 'fflate';
import LayerGroup from 'ol/layer/Group.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import WKB from 'ol/format/WKB.js';

// ponytail: sql.js carga la DB completa en memoria WASM32; VFS chunked sobre
// IndexedDB si algún día se requieren los 5GB de la spec.
export const MAX_GPKG_BYTES = 1.5 * 1024 * 1024 * 1024;

const BLANK_TILE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

let sqlJsPromise = null;
let activeDb = null; // DB del basemap offline activo; se cierra al reemplazar

const IDB_NAME = 'visor-offline-basemap';
const IDB_STORE = 'gpkg';
const IDB_KEY = 'current';
export const OFFLINE_FLAG = 'offlineBasemapActive'; // sessionStorage: sobrevive refresh, no restart

function idbOpen() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbRequest(mode, fn) {
  return idbOpen().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, mode);
        const req = fn(tx.objectStore(IDB_STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      })
  );
}

function getSqlJs() {
  if (!sqlJsPromise) {
    // wasm servido desde public/ (copiado en build), fetch runtime; el build browser pide
    // sql-wasm-browser.wasm pero es byte-identico a sql-wasm.wasm, el asset ya comiteado
    sqlJsPromise = initSqlJs({ locateFile: () => '/sql-wasm.wasm' });
  }
  return sqlJsPromise;
}

class GeopackageService {
  /** Descarga con progreso vía ReadableStream. @returns {Promise<ArrayBuffer>} */
  async fetchGpkg(url, onProgress) {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Descarga falló (HTTP ${resp.status})`);
    const total = Number(resp.headers.get('content-length')) || 0;
    const reader = resp.body.getReader();
    const chunks = [];
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (onProgress) onProgress(total ? Math.round((received / total) * 100) : null);
    }
    const buffer = new Uint8Array(received);
    let offset = 0;
    for (const c of chunks) {
      buffer.set(c, offset);
      offset += c.length;
    }
    return buffer.buffer;
  }

  /** Lee un File/Blob local con progreso (FileReader.onprogress). */
  readGpkgFile(file, onProgress) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onprogress = (e) => {
        if (onProgress && e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
      reader.onload = () => resolve(reader.result);
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Abre el .gpkg y construye la capa base OL.
   * Lanza Error con mensaje legible si el archivo es inválido.
   * @param {ArrayBuffer} buffer
   * @returns {Promise<LayerGroup>}
   */
  async buildGpkgLayer(buffer) {
    const SQL = await getSqlJs();
    let db;
    try {
      db = new SQL.Database(new Uint8Array(buffer));
    } catch (err) {
      throw new Error('El archivo no es un GeoPackage/SQLite válido');
    }

    try {
      const contents = db.exec(
        "SELECT table_name, data_type FROM gpkg_contents WHERE data_type IN ('tiles','features')"
      );
      if (!contents.length) {
        throw new Error('El GeoPackage no contiene tablas de tiles ni de features');
      }
      const tileLayers = [];
      const featureLayers = [];
      for (const [tableName, dataType] of contents[0].values) {
        if (dataType === 'tiles') {
          tileLayers.push(this._buildTileLayerForTable(db, tableName));
        } else {
          featureLayers.push(this._buildFeatureLayer(db, tableName));
        }
      }
      // req. 18: vector sobre raster; entre vectores, orden de gpkg_contents (req. 17)
      const layers = [...tileLayers, ...featureLayers].filter(Boolean);
      if (!layers.length) throw new Error('El GeoPackage no contiene capas renderizables');
      const group = new LayerGroup({ layers, zIndex: 0 });

      // reemplazo exitoso: cerrar DB del basemap offline anterior
      if (activeDb) activeDb.close();
      activeDb = db;
      return group;
    } catch (err) {
      db.close();
      throw err;
    }
  }

  _buildTileLayerForTable(db, tableName) {
    const tmsRow = db.exec(
      `SELECT srs_id, min_x, min_y, max_x, max_y FROM gpkg_tile_matrix_set WHERE table_name='${tableName}'`
    )[0].values[0];
    const [srsId, minX, minY, maxX, maxY] = tmsRow;
    if (srsId !== 3857 && srsId !== 900913) {
      throw new Error(`CRS no soportado (EPSG:${srsId}); se requiere EPSG:3857`);
    }

    const matrixRows = db.exec(
      `SELECT zoom_level, tile_width, pixel_x_size FROM gpkg_tile_matrix WHERE table_name='${tableName}' ORDER BY zoom_level`
    )[0].values;

    // resolutions indexadas por zoom_level; huecos rellenados geométricamente (/2)
    const maxZoom = matrixRows[matrixRows.length - 1][0];
    const byZoom = new Map(matrixRows.map((r) => [r[0], r]));
    const resolutions = [];
    const baseRes = matrixRows[0][2] * Math.pow(2, matrixRows[0][0]);
    for (let z = 0; z <= maxZoom; z++) {
      resolutions.push(byZoom.has(z) ? byZoom.get(z)[2] : baseRes / Math.pow(2, z));
    }
    const tileSize = matrixRows[0][1];
    const tileGrid = new TileGrid({
      extent: [minX, minY, maxX, maxY],
      origin: [minX, maxY], // gpkg: tile_row 0 = fila superior
      resolutions,
      tileSize,
    });

    const probe = db.exec(`SELECT tile_data FROM "${tableName}" LIMIT 1`);
    if (!probe.length) throw new Error('La tabla de tiles está vacía');
    const first = probe[0].values[0][0];
    const isRaster =
      (first[0] === 0x89 && first[1] === 0x50) || // PNG
      (first[0] === 0xff && first[1] === 0xd8); // JPEG

    const getTile = (z, x, y) => {
      const res = db.exec(
        `SELECT tile_data FROM "${tableName}" WHERE zoom_level=${z} AND tile_column=${x} AND tile_row=${y}`
      );
      return res.length ? res[0].values[0][0] : null;
    };

    return isRaster
      ? this._buildRasterLayer(tileGrid, getTile)
      : this._buildVectorLayer(tileGrid, getTile);
  }

  _buildFeatureLayer(db, tableName) {
    const meta = db.exec(
      `SELECT column_name, srs_id FROM gpkg_geometry_columns WHERE table_name='${tableName}'`
    );
    if (!meta.length) return null;
    const [geomCol, srsId] = meta[0].values[0];
    const dataProjection = this._resolveProjection(db, srsId);
    const format = new WKB();
    const features = [];
    const stmt = db.prepare(`SELECT "${geomCol}" FROM "${tableName}"`);
    try {
      while (stmt.step()) {
        const wkb = this._stripGpkgHeader(stmt.get()[0]);
        if (!wkb) continue; // geometría vacía o header inválido
        try {
          features.push(format.readFeature(wkb.slice().buffer, {
            dataProjection,
            featureProjection: 'EPSG:3857',
          }));
        } catch (err) {
          // fila corrupta o tipo WKB no soportado: skip, no abortar la tabla
        }
      }
    } finally {
      stmt.free();
    }
    return new VectorLayer({ source: new VectorSource({ features }) });
  }

  // GeoPackage Binary header (OGC gpb_data_blob_format): 'GP' + version + flags + srs_id + envelope
  _stripGpkgHeader(blob) {
    if (!blob || blob.length < 8 || blob[0] !== 0x47 || blob[1] !== 0x50) return null;
    const flags = blob[3];
    if (flags & 0x10) return null; // bit 4: empty geometry, sin cuerpo WKB
    const envCode = (flags >> 1) & 0x07;
    if (envCode > 4) return null; // código de envelope inválido según spec OGC
    return blob.subarray(8 + [0, 32, 48, 48, 64][envCode]);
  }

  // Fase 1: sólo CRS nativos de OL; Fase 2 generaliza vía proj4 + gpkg_spatial_ref_sys
  _resolveProjection(db, srsId) {
    if (srsId === 3857 || srsId === 900913) return 'EPSG:3857';
    if (srsId === 4326) return 'EPSG:4326';
    throw new Error(`CRS no soportado (EPSG:${srsId}); se requiere EPSG:3857`);
  }

  /**
   * Persiste el gpkg activado (Blob único, reemplaza el anterior) y marca el flag.
   * QuotaExceededError u otro fallo de storage NO revierte la activación: solo avisa.
   */
  async persistGpkg(buffer) {
    try {
      await idbRequest('readwrite', (store) => store.put(new Blob([buffer]), IDB_KEY));
      sessionStorage.setItem(OFFLINE_FLAG, '1');
    } catch (err) {
      console.warn('[GeopackageService] No se pudo persistir el GeoPackage:', err);
      alert(
        'El mapa offline quedó activo pero no pudo guardarse en el navegador; ' +
        'no estará disponible tras recargar la página.'
      );
    }
  }

  clearOfflineFlag() {
    sessionStorage.removeItem(OFFLINE_FLAG);
  }

  /**
   * Restaura el basemap offline tras refresh. Silencioso en fallo (queda OSM).
   */
  async restoreOfflineBasemap(mapServiceInstance) {
    if (!sessionStorage.getItem(OFFLINE_FLAG)) return;
    try {
      const blob = await idbRequest('readonly', (store) => store.get(IDB_KEY));
      if (!blob) return;
      const buffer = await blob.arrayBuffer();
      const layer = await this.buildGpkgLayer(buffer);
      mapServiceInstance.applyBaseLayerDirect(layer);
    } catch (err) {
      console.warn('[GeopackageService] No se pudo restaurar el basemap offline:', err);
      sessionStorage.removeItem(OFFLINE_FLAG);
    }
  }

  _buildRasterLayer(tileGrid, getTile) {
    const source = new XYZ({
      projection: 'EPSG:3857',
      tileGrid,
      tileUrlFunction: ([z, x, y]) => `gpkg://${z}/${x}/${y}`,
      tileLoadFunction: (tile) => {
        const [z, x, y] = tile.getTileCoord();
        const data = getTile(z, x, y);
        const img = tile.getImage();
        if (!data) {
          img.src = BLANK_TILE; // tile ausente = transparente, NO error (evita rollback falso)
          return;
        }
        const url = URL.createObjectURL(new Blob([data]));
        img.onload = () => URL.revokeObjectURL(url);
        img.src = url;
      },
    });
    return new TileLayer({ source });
  }

  _buildVectorLayer(tileGrid, getTile) {
    const format = new MVT();
    const source = new VectorTileSource({
      projection: 'EPSG:3857',
      tileGrid,
      format,
      tileUrlFunction: ([z, x, y]) => `gpkg://${z}/${x}/${y}`,
      tileLoadFunction: (tile) => {
        tile.setLoader((extent, resolution, projection) => {
          const [z, x, y] = tile.getTileCoord();
          let data = getTile(z, x, y);
          if (!data) {
            tile.setFeatures([]);
            return;
          }
          if (data[0] === 0x1f && data[1] === 0x8b) data = gunzipSync(data);
          const features = format.readFeatures(data.slice().buffer, {
            extent,
            featureProjection: projection,
          });
          tile.setFeatures(features);
        });
      },
    });
    // estilo default de OL — los gpkg vector tiles no traen style Mapbox estándar
    return new VectorTileLayer({ source });
  }
}

export const geopackageService = new GeopackageService();
